import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton, useTheme, alpha } from '@mui/material';
import Iconify from '../iconify';

// Custom Toast Component with modern design
export const CustomToast = ({ type, message, onClose }) => {
  const theme = useTheme();

  const getToastConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: 'eva:checkmark-circle-2-fill',
          color: theme.palette.success.main,
          bgColor: alpha(theme.palette.success.main, 0.12),
          borderColor: theme.palette.success.main,
        };
      case 'error':
        return {
          icon: 'eva:alert-circle-fill',
          color: theme.palette.error.main,
          bgColor: alpha(theme.palette.error.main, 0.12),
          borderColor: theme.palette.error.main,
        };
      case 'warning':
        return {
          icon: 'eva:alert-triangle-fill',
          color: theme.palette.warning.main,
          bgColor: alpha(theme.palette.warning.main, 0.12),
          borderColor: theme.palette.warning.main,
        };
      case 'info':
        return {
          icon: 'eva:info-fill',
          color: theme.palette.info.main,
          bgColor: alpha(theme.palette.info.main, 0.12),
          borderColor: theme.palette.info.main,
        };
      default:
        return {
          icon: 'eva:info-fill',
          color: theme.palette.primary.main,
          bgColor: alpha(theme.palette.primary.main, 0.12),
          borderColor: theme.palette.primary.main,
        };
    }
  };

  const config = getToastConfig();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        p: 2.5,
        minWidth: 350,
        maxWidth: 480,
        backgroundColor: config.bgColor,
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(config.borderColor, 0.2)}`,
        borderRadius: 2,
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: 4,
          height: '100%',
          backgroundColor: config.color,
          borderRadius: '0 2px 2px 0',
        },
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: alpha(config.color, 0.15),
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        <Iconify
          icon={config.icon}
          width={18}
          height={18}
          sx={{ color: config.color }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 500,
            lineHeight: 1.4,
            wordBreak: 'break-word',
          }}
        >
          {message}
        </Typography>
      </Box>

      {/* Close Button */}
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          color: theme.palette.text.secondary,
          flexShrink: 0,
          mt: -0.5,
          mr: -0.5,
          '&:hover': {
            backgroundColor: alpha(theme.palette.text.primary, 0.08),
          },
        }}
      >
        <Iconify icon="eva:close-fill" width={18} height={18} />
      </IconButton>
    </Box>
  );
};

CustomToast.propTypes = {
  type: PropTypes.oneOf(['success', 'error', 'warning', 'info']).isRequired,
  message: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CustomToast;
